package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.PerehdytysMateriaali
import com.kaihuoy.shared.data.User
import com.kaihuoy.components.common.*
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import com.kaihuoy.web.WebFirebaseRepository
import com.kaihuoy.web.router.AppRouterState
import kotlinx.coroutines.CoroutineScope
import kotlin.js.Date

@Composable
fun AllUsersScreen() {
    var users by remember { mutableStateOf<List<User>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    val repository = remember { WebFirebaseRepository() }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        try {
            FirebaseService.database.reference("users").valueEvents.collect { snapshot ->
                val userList = mutableListOf<User>()

                // Turvallinen käsittely
                snapshot.children.forEach { child ->
                    userList.add(child.value())
                }

                users = userList.sortedBy { it.displayName }
                isLoading = false
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            // Normaali peruutus
        } catch (e: Exception) {
            errorMessage = "Käyttäjien lataus epäonnistui: ${e.message}"
            isLoading = false
        }
    }

    Div(attrs = { style { } }) {
        if (isLoading) {
            Div(attrs = { style { padding(100.px); textAlign("center") } }) {
                Text("Ladataan käyttäjiä...")
            }
        } else if (errorMessage != null) {
            P(attrs = { style { color(KaihuTheme.Danger) } }) {
                Text(errorMessage ?: "")
            }
        } else if (users.isEmpty()) {
            P(attrs = { style { textAlign("center"); padding(60.px) } }) {
                Text("Ei käyttäjiä vielä tietokannassa.")
            }
        } else {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                }
            }) {
                users.forEach { user ->
                    UserListItem(user, repository, scope)
                }
            }
        }

        Div(attrs = { style { marginTop(40.px) } }) {
            // Poistettu turha takaisin-nappi, kun ollaan jo välilehdellä
        }
    }
}

// Yksittäinen käyttäjä-rivi + roolin muokkaus + Poista-nappi
@Composable
private fun UserListItem(
    user: User,
    repository: WebFirebaseRepository,
    scope: CoroutineScope
) {
    var isDeleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }
    var isUpdatingRole by remember { mutableStateOf(false) }
    var isSendingReset by remember { mutableStateOf(false) }
    var showLuetutMat by remember { mutableStateOf(false) }
    var luetutIds by remember { mutableStateOf<Map<String, Long>>(emptyMap()) }
    var kaikki by remember { mutableStateOf<List<PerehdytysMateriaali>>(emptyList()) }
    var isLoadingLuetut by remember { mutableStateOf(false) }

    LaunchedEffect(showLuetutMat) {
        if (!showLuetutMat || isLoadingLuetut) return@LaunchedEffect
        isLoadingLuetut = true
        try {
            launch {
                FirebaseService.database.reference("materiaalit_luettu").child(user.uid).valueEvents.collect { s ->
                    luetutIds = s.children.mapNotNull { child ->
                        val key = child.key ?: return@mapNotNull null
                        val ts = try { child.value<Double>().toLong() } catch (_: Exception) { 0L }
                        key to ts
                    }.toMap()
                }
            }
            launch {
                FirebaseService.database.reference("perehdytys_materiaalit").valueEvents.collect { s ->
                    kaikki = s.children.mapNotNull { child ->
                        try { val m = child.value<PerehdytysMateriaali>(); if (m.id.isBlank()) m.copy(id = child.key ?: "") else m }
                        catch (_: Exception) { null }
                    }
                    isLoadingLuetut = false
                }
            }
        } catch (_: Exception) { isLoadingLuetut = false }
    }

    Div(attrs = {
        style {
            backgroundColor(KaihuTheme.Surface)
            padding(20.px)
            borderRadius(12.px)
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.08)")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.FlexStart)
            flexWrap(FlexWrap.Wrap)
            gap(12.px)
        }
    }) {
        Div(attrs = { style { flex(1) } }) {
            H4(attrs = { style { margin(0.px, 0.px, 4.px, 0.px) } }) {
                Text(user.displayName)
            }
            P(attrs = { style { margin(0.px, 0.px, 10.px, 0.px); color(KaihuTheme.TextSecondary) } }) {
                Text(user.email)
            }

            // Roolinapit
            Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
                RoleToggle(
                    label = "Ylläpitäjä",
                    active = user.isAdmin,
                    enabled = !isUpdatingRole,
                    activeColor = KaihuTheme.PrimaryTeal,
                    onClick = {
                        isUpdatingRole = true
                        scope.launch {
                            repository.updateUserAdminStatus(user.uid, !user.isAdmin) { success ->
                                isUpdatingRole = false
                                if (!success) com.kaihuoy.components.common.AppFeedback.showError("Roolin päivitys epäonnistui.")
                            }
                        }
                    }
                )
                RoleToggle(
                    label = "Huolto",
                    active = user.isHuolto,
                    enabled = !isUpdatingRole,
                    activeColor = Color("#F59E0B"),
                    onClick = {
                        isUpdatingRole = true
                        scope.launch {
                            repository.updateUserHuoltoStatus(user.uid, !user.isHuolto) { success ->
                                isUpdatingRole = false
                                if (!success) com.kaihuoy.components.common.AppFeedback.showError("Roolin päivitys epäonnistui.")
                            }
                        }
                    }
                )
                RoleToggle(
                    label = "Putkityö",
                    active = user.isPutkityo,
                    enabled = !isUpdatingRole,
                    activeColor = Color("#0EA5E9"),
                    onClick = {
                        isUpdatingRole = true
                        scope.launch {
                            repository.updateUserPutkityoStatus(user.uid, !user.isPutkityo) { success ->
                                isUpdatingRole = false
                                if (!success) com.kaihuoy.components.common.AppFeedback.showError("Roolin päivitys epäonnistui.")
                            }
                        }
                    }
                )
            }

            if (deleteError != null) {
                P(attrs = { style { color(KaihuTheme.Danger); fontSize(0.85.em); margin(6.px, 0.px, 0.px, 0.px) } }) {
                    Text(deleteError!!)
                }
            }
        }

        Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px) } }) {
            StyledButton(
                text = if (showLuetutMat) "Piilota materiaalit" else "Luetut materiaalit",
                icon = if (showLuetutMat) "chevron-up" else "book-open",
                backgroundColor = KaihuTheme.Secondary,
                onClick = { showLuetutMat = !showLuetutMat }
            )
            StyledButton(
                text = if (isSendingReset) "Lähetetään..." else "Nollaa salasana",
                icon = if (isSendingReset) null else "key",
                enabled = !isSendingReset,
                backgroundColor = KaihuTheme.Amber,
                onClick = {
                    com.kaihuoy.components.common.AppFeedback.confirm(
                        title = "Nollataan salasana?",
                        message = "Lähetetäänkö salasanan palautuslinkki osoitteeseen ${user.email}?",
                        confirmText = "Lähetä",
                        confirmColor = KaihuTheme.Amber
                    ) {
                        isSendingReset = true
                        scope.launch {
                            repository.sendPasswordResetEmail(user.email) { success, _ ->
                                isSendingReset = false
                                if (success) com.kaihuoy.components.common.AppFeedback.showSuccess("Palautusviesti lähetetty osoitteeseen ${user.email}")
                                else com.kaihuoy.components.common.AppFeedback.showError("Lähetys epäonnistui")
                            }
                        }
                    }
                }
            )
            LDelButton(
                text = if (isDeleting) "Poistetaan..." else "Poista",
            onClick = {
                com.kaihuoy.components.common.AppFeedback.confirm(
                    title = "Poistetaanko käyttäjä?",
                    message = "Haluatko varmasti poistaa käyttäjän ${user.displayName}? Tämä toiminto on peruuttamaton.",
                    confirmText = "Poista",
                    confirmColor = KaihuTheme.Danger
                ) {
                    isDeleting = true
                    deleteError = null
                    scope.launch {
                        repository.deleteUser(user.uid) { success ->
                            isDeleting = false
                            if (success) {
                                com.kaihuoy.components.common.AppFeedback.showSuccess("Käyttäjä ${user.displayName} poistettiin onnistuneesti.")
                            } else {
                                deleteError = "Poisto epäonnistui"
                                com.kaihuoy.components.common.AppFeedback.showError("Poisto epäonnistui. Tarkista console virheistä.")
                            }
                        }
                    }
                }
            }
        )
        } // Div napit
    }

    if (showLuetutMat) {
        Div(attrs = {
            style {
                marginTop(12.px); backgroundColor(KaihuTheme.Background); borderRadius(12.px); padding(16.px)
                property("border", "1px solid ${KaihuTheme.Divider}")
            }
        }) {
            Div(attrs = { style { fontWeight("700"); color(KaihuTheme.TextPrimary); marginBottom(10.px); fontSize(0.9.em) } }) {
                TIcon("book-open"); Text("  Luetut materiaalit")
            }
            when {
                isLoadingLuetut -> Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em) } }) { Text("Ladataan...") }
                kaikki.isEmpty() -> Div(attrs = { style { color(KaihuTheme.TextLight); fontSize(0.88.em); fontStyle("italic") } }) { Text("Ei materiaaleja.") }
                else -> {
                    val sorted = kaikki.sortedBy { it.otsikko }
                    Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) } }) {
                        sorted.forEach { mat ->
                            val luettu = mat.id in luetutIds
                            val ts = luetutIds[mat.id] ?: 0L
                            val dateStr = if (luettu && ts > 0L) Date(ts.toDouble()).toLocaleString("fi-FI") else ""
                            Div(attrs = {
                                style {
                                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                                    padding(8.px, 10.px); borderRadius(8.px); gap(8.px)
                                    if (luettu) { backgroundColor(Color("#0d2b1e")); property("border", "1px solid #10B98133") }
                                    else { backgroundColor(Color("#2b0d0d")); property("border", "1px solid #EF444433") }
                                }
                            }) {
                                Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                                    Span(attrs = { style { color(if (luettu) Color("#10B981") else Color("#EF4444")) } }) {
                                        TIcon(if (luettu) "circle-check" else "circle-x")
                                    }
                                    Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.9.em); fontWeight("600") } }) { Text(mat.otsikko) }
                                }
                                if (dateStr.isNotBlank()) Span(attrs = { style { color(KaihuTheme.TextLight); fontSize(0.8.em) } }) { Text(dateStr) }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RoleToggle(
    label: String,
    active: Boolean,
    enabled: Boolean,
    activeColor: CSSColorValue,
    onClick: () -> Unit
) {
    Span(attrs = {
        onClick { if (enabled) onClick() }
        style {
            padding(6.px, 14.px)
            borderRadius(999.px)
            fontSize(0.85.em)
            fontWeight("600")
            cursor(if (enabled) "pointer" else "default")
            property("transition", "all 0.15s")
            backgroundColor(if (active) activeColor else KaihuTheme.Divider)
            color(if (active) Color.white else KaihuTheme.TextSecondary)
            property("border", "2px solid ${if (active) "transparent" else KaihuTheme.Border}")
            opacity(if (enabled) 1.0 else 0.6)
        }
    }) {
        if (active) TIcon("check")
        Text(if (active) " $label" else label)
    }
}